
import React, { useState } from 'react';
import { useScheduleAlerts } from '../hooks/useScheduleAlerts';
import { AlertCard } from './AlertCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { WeekSelector } from './WeekSelector';
import { TeamSelector } from './TeamSelector';
import { AlertTypeFilter } from './AlertTypeFilter';
import { Loader2, RefreshCw } from 'lucide-react';
import { TeamVisibilityGrid } from './TeamVisibilityGrid';
import { DateRangeSelector } from './DateRangeSelector';
import { useLanguage } from '@/contexts/LanguageContext';

export const AlertsList: React.FC = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<string>('active');
  const [selectedAlertType, setSelectedAlertType] = useState<string | null>(null);
  const {
    activeAlerts,
    resolvedAlerts,
    isLoading,
    isGenerating,
    selectedWeek,
    selectedTeamId,
    handleResolveAlert,
    handleGenerateAlerts,
    handleSelectWeek,
    handleSelectTeam,
    dateRangeType,
    customDateRange,
    startDate,
    endDate,
    handleSelectDateRangeType,
    handleSelectCustomDateRange,
    refetch
  } = useScheduleAlerts();

  const handleRefresh = () => {
    refetch();
  };

  // Filter alerts by selected type
  const filterAlertsByType = (alerts: any[]) => {
    if (!selectedAlertType) return alerts;
    return alerts.filter(alert => alert.alert_type === selectedAlertType);
  };

  const filteredActiveAlerts = filterAlertsByType(activeAlerts);
  const filteredResolvedAlerts = filterAlertsByType(resolvedAlerts);

  return (
    <div className="space-y-4">
      <div className="flex flex-col space-y-2 sm:flex-row sm:justify-between sm:items-center">
        <WeekSelector 
          selectedWeek={selectedWeek} 
          onSelectWeek={handleSelectWeek} 
        />
        
        <div className="flex space-x-2">
          <Button 
            onClick={handleRefresh} 
            variant="outline" 
            size="sm"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {t('refresh') || 'Refresh'}
          </Button>
          
          <Button 
            onClick={handleGenerateAlerts} 
            variant="default" 
            size="sm"
            disabled={isGenerating}
          >
            {isGenerating ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            {t('generateAlerts') || 'Generate Alerts'}
          </Button>
        </div>
      </div>
      
      <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:justify-between md:items-center">
        <DateRangeSelector
          dateRangeType={dateRangeType}
          customDateRange={customDateRange}
          startDate={startDate}
          endDate={endDate}
          onSelectDateRangeType={handleSelectDateRangeType}
          onSelectCustomDateRange={handleSelectCustomDateRange}
        />
        
        <TeamSelector 
          selectedTeamId={selectedTeamId} 
          onSelectTeam={handleSelectTeam}
        />
      </div>

      <AlertTypeFilter
        selectedType={selectedAlertType}
        onSelectType={setSelectedAlertType}
      />
      
      {selectedTeamId && (
        <TeamVisibilityGrid teamId={selectedTeamId} />
      )}
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="active" className="relative">
            {t('activeAlerts') || 'Active Alerts'}
            {filteredActiveAlerts.length > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[1.5rem]">
                {filteredActiveAlerts.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="resolved">
            {t('resolvedAlerts') || 'Resolved Alerts'}
            {filteredResolvedAlerts.length > 0 && (
              <span className="ml-2 bg-green-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[1.5rem]">
                {filteredResolvedAlerts.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="active" className="mt-4">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredActiveAlerts.length > 0 ? (
            <div className="space-y-4">
              {filteredActiveAlerts.map(alert => (
                <AlertCard 
                  key={alert.id} 
                  alert={alert} 
                  onResolve={handleResolveAlert}
                />
              ))}
            </div>
          ) : (
            <div className="bg-muted rounded-lg p-6 text-center">
              <p className="text-muted-foreground">
                {selectedAlertType 
                  ? (t('noActiveAlertsOfType') || `No active ${selectedAlertType} alerts found for the selected period.`)
                  : (t('noActiveAlerts') || 'No active alerts found for the selected period.')
                }
              </p>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="resolved" className="mt-4">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredResolvedAlerts.length > 0 ? (
            <div className="space-y-4">
              {filteredResolvedAlerts.map(alert => (
                <AlertCard key={alert.id} alert={alert} />
              ))}
            </div>
          ) : (
            <div className="bg-muted rounded-lg p-6 text-center">
              <p className="text-muted-foreground">
                {selectedAlertType 
                  ? (t('noResolvedAlertsOfType') || `No resolved ${selectedAlertType} alerts found for the selected period.`)
                  : (t('noResolvedAlerts') || 'No resolved alerts found for the selected period.')
                }
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
